/**
 * Serialize basket-mutating SCAPI calls on the cart/checkout so their responses
 * apply to the shared React Query basket cache in FIFO order.
 *
 * WHY (KL-1029): every basket mutation — `removeItemFromBasket`,
 * `updateShippingMethodForShipment`, `addCouponToBasket` /
 * `removeCouponFromBasket`, and the deposit `updateBasket` PATCH — writes the
 * SAME `getCustomerBaskets` React Query cache via the Commerce SDK's
 * `onSuccess` → `setQueryData`, with last-write-wins semantics and no
 * serialization between them. When a shopper removes a line item while another
 * basket call is in flight (change delivery method, apply promo code, or the
 * deposit auto-sync effect that fires on `productItems` changes), a response
 * built on the PRE-removal basket can land last and resurrect the removed item
 * (and its option/child line items).
 *
 * Serializing every basket mutation through a single promise chain guarantees a
 * mutation starts only after the previous one has fully settled (including its
 * synchronous cache write). SCAPI therefore always processes each request
 * against the latest basket, so no in-flight response can restore a line item
 * that a prior queued mutation already removed.
 *
 * This is a client-runtime queue: all callers are user-driven event handlers /
 * effects that never run during SSR, so the module-level chain is safe.
 */

let chain = Promise.resolve();

/**
 * Enqueue a basket mutation. The task runs only after all previously enqueued
 * tasks have settled. The returned promise resolves/rejects with the task's own
 * result so callers keep their existing try/catch and `.then/.catch` handling.
 *
 * @template T
 * @param {() => Promise<T>} task - Runs the actual mutation (e.g. `mutateAsync`).
 * @returns {Promise<T>} Resolves/rejects with the task result.
 */
export function enqueueBasketMutation(task) {
  const run = chain.then(() => task());

  // Advance the chain once this task settles (success OR failure) so a rejected
  // mutation never wedges the queue. The caller still sees the real outcome via
  // the returned `run` promise.
  chain = run.then(
    () => undefined,
    () => undefined
  );

  return run;
}

/**
 * Commerce SDK `getCustomerBaskets` query keys:
 * `['/commerce-sdk-react', '/organizations/', orgId, '/customers/', customerId, '/baskets', …]`
 *
 * @param {unknown} queryKey
 * @returns {boolean}
 */
export function isCustomerBasketsQueryKey(queryKey) {
  if (!Array.isArray(queryKey)) {
    return false;
  }

  const parts = queryKey.flat(Infinity).map(String);

  return parts.includes('/customers/') && parts.includes('/baskets');
}

/**
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @returns {Array<[unknown, unknown]>}
 */
export function snapshotCustomerBasketsQueries(queryClient) {
  if (!queryClient) {
    return [];
  }

  return queryClient.getQueriesData({
    predicate: (query) => isCustomerBasketsQueryKey(query.queryKey),
  });
}

/**
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {Array<[unknown, unknown]>} snapshot
 */
export function restoreCustomerBasketsQueries(queryClient, snapshot) {
  if (!queryClient || !snapshot?.length) {
    return;
  }

  snapshot.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
}

/**
 * Extract customerId from a getCustomerBaskets query key
 * (`… '/customers/', customerId, '/baskets', …`).
 *
 * @param {unknown} queryKey
 * @returns {string|null}
 */
function customerIdFromCustomerBasketsQueryKey(queryKey) {
  if (!Array.isArray(queryKey)) {
    return null;
  }

  const parts = queryKey.flat(Infinity).map(String);
  const idx = parts.indexOf('/customers/');

  if (idx === -1 || idx + 1 >= parts.length) {
    return null;
  }

  const customerId = parts[idx + 1];

  if (!customerId || customerId.startsWith('/')) {
    return null;
  }

  return customerId;
}

/**
 * Read the live basket from the shared `getCustomerBaskets` React Query cache.
 * Prefer an exact `basketId` match; otherwise the first basket with an id.
 *
 * When `customerId` is provided, only that customer's cached query is scanned —
 * inactive queries from a prior login must not supply a basket id (logout /
 * account switch).
 *
 * Used inside queued mutations so decisions (e.g. last-item `deleteBasket`) are
 * based on cache state at execution time, not a stale render snapshot.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string|null|undefined} [basketId]
 * @param {string|null|undefined} [customerId]
 * @returns {Object|null}
 */
export function getBasketFromCustomerBasketsCache(queryClient, basketId, customerId) {
  if (!queryClient) {
    return null;
  }

  const entries = queryClient.getQueriesData({
    predicate: (query) => {
      if (!isCustomerBasketsQueryKey(query.queryKey)) {
        return false;
      }

      if (!customerId) {
        return true;
      }

      return customerIdFromCustomerBasketsQueryKey(query.queryKey) === customerId;
    },
  });

  for (const [, data] of entries) {
    const baskets = data?.baskets;

    if (!Array.isArray(baskets) || baskets.length === 0) {
      continue;
    }

    if (basketId) {
      const match = baskets.find((basket) => basket?.basketId === basketId);

      if (match) {
        return match;
      }

      // Prefer exact id only — do not fall through to another basket/query.
      continue;
    }

    const first = baskets.find((basket) => basket?.basketId);

    if (first) {
      return first;
    }
  }

  return null;
}

/**
 * Drop a basket from the customer-baskets cache so the flyout does not flash
 * deleted lines while `deleteBasket` invalidates and refetches.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} basketId
 */
export function evictBasketFromCustomerBasketsCache(queryClient, basketId) {
  if (!queryClient || !basketId) {
    return;
  }

  queryClient.setQueriesData(
    {predicate: (query) => isCustomerBasketsQueryKey(query.queryKey)},
    (old) => {
      if (!old?.baskets) {
        return old;
      }

      const baskets = old.baskets.filter((basket) => basket.basketId !== basketId);

      return {...old, baskets, total: baskets.length};
    }
  );
}
